import { storableError } from '../../util/errors';

import { updateSavedSearchesCount } from '../../ducks/user.duck';

// ================ Action types ================ //

export const FETCH_SAVED_SEARCHES_REQUEST =
  'app/SavedSearchesPage/FETCH_SAVED_SEARCHES_REQUEST';
export const FETCH_SAVED_SEARCHES_SUCCESS =
  'app/SavedSearchesPage/FETCH_SAVED_SEARCHES_SUCCESS';
export const FETCH_SAVED_SEARCHES_ERROR =
  'app/SavedSearchesPage/FETCH_SAVED_SEARCHES_ERROR';

export const SAVE_SEARCH_REQUEST = 'app/SavedSearchesPage/SAVE_SEARCH_REQUEST';
export const SAVE_SEARCH_SUCCESS = 'app/SavedSearchesPage/SAVE_SEARCH_SUCCESS';
export const SAVE_SEARCH_ERROR = 'app/SavedSearchesPage/SAVE_SEARCH_ERROR';

export const DELETE_SAVED_SEARCH_REQUEST =
  'app/SavedSearchesPage/DELETE_SAVED_SEARCH_REQUEST';
export const DELETE_SAVED_SEARCH_SUCCESS =
  'app/SavedSearchesPage/DELETE_SAVED_SEARCH_SUCCESS';
export const DELETE_SAVED_SEARCH_ERROR =
  'app/SavedSearchesPage/DELETE_SAVED_SEARCH_ERROR';

export const UPDATE_SAVED_SEARCH_REQUEST =
  'app/SavedSearchesPage/UPDATE_SAVED_SEARCH_REQUEST';
export const UPDATE_SAVED_SEARCH_SUCCESS =
  'app/SavedSearchesPage/UPDATE_SAVED_SEARCH_SUCCESS';
export const UPDATE_SAVED_SEARCH_ERROR =
  'app/SavedSearchesPage/UPDATE_SAVED_SEARCH_ERROR';

// ================ Reducer ================ //

const initialState = {
  savedSearches: [],
  queryInProgress: false,
  queryError: null,
  saveInProgress: false,
  saveError: null,
  deleteInProgress: false,
  deleteError: null,
  updateInProgress: false,
  updateError: null,
};

const savedSearchesPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_SAVED_SEARCHES_REQUEST:
      return {
        ...state,
        queryInProgress: true,
        queryError: null,
      };
    case FETCH_SAVED_SEARCHES_SUCCESS:
      return {
        ...state,
        savedSearches: Array.isArray(payload.savedSearches)
          ? payload.savedSearches.filter(search => search && search.id)
          : [],
        queryInProgress: false,
      };
    case FETCH_SAVED_SEARCHES_ERROR:
      return {
        ...state,
        queryInProgress: false,
        queryError: payload,
      };

    case SAVE_SEARCH_REQUEST:
      return {
        ...state,
        saveInProgress: true,
        saveError: null,
      };
    case SAVE_SEARCH_SUCCESS:
      return {
        ...state,
        savedSearches:
          payload.savedSearch && payload.savedSearch.id
            ? [...state.savedSearches, payload.savedSearch]
            : state.savedSearches,
        saveInProgress: false,
      };
    case SAVE_SEARCH_ERROR:
      return {
        ...state,
        saveInProgress: false,
        saveError: payload,
      };

    case DELETE_SAVED_SEARCH_REQUEST:
      return {
        ...state,
        deleteInProgress: true,
        deleteError: null,
      };
    case DELETE_SAVED_SEARCH_SUCCESS:
      return {
        ...state,
        savedSearches: state.savedSearches.filter(
          search => search && String(search.id) !== String(payload.searchId)
        ),
        deleteInProgress: false,
      };
    case DELETE_SAVED_SEARCH_ERROR:
      return {
        ...state,
        deleteInProgress: false,
        deleteError: payload,
      };

    case UPDATE_SAVED_SEARCH_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateError: null,
      };
    case UPDATE_SAVED_SEARCH_SUCCESS:
      return {
        ...state,
        savedSearches: state.savedSearches.map(search =>
          search && String(search.id) === String(payload.savedSearch.id)
            ? payload.savedSearch
            : search
        ),
        updateInProgress: false,
      };
    case UPDATE_SAVED_SEARCH_ERROR:
      return {
        ...state,
        updateInProgress: false,
        updateError: payload,
      };

    default:
      return state;
  }
};

export default savedSearchesPageReducer;

// ================ Action creators ================ //

export const fetchSavedSearchesRequest = () => ({
  type: FETCH_SAVED_SEARCHES_REQUEST,
});
export const fetchSavedSearchesSuccess = savedSearches => ({
  type: FETCH_SAVED_SEARCHES_SUCCESS,
  payload: { savedSearches },
});
export const fetchSavedSearchesError = e => ({
  type: FETCH_SAVED_SEARCHES_ERROR,
  error: true,
  payload: e,
});

export const saveSearchRequest = () => ({ type: SAVE_SEARCH_REQUEST });
export const saveSearchSuccess = savedSearch => ({
  type: SAVE_SEARCH_SUCCESS,
  payload: { savedSearch },
});
export const saveSearchError = e => ({
  type: SAVE_SEARCH_ERROR,
  error: true,
  payload: e,
});

export const deleteSavedSearchRequest = () => ({
  type: DELETE_SAVED_SEARCH_REQUEST,
});
export const deleteSavedSearchSuccess = searchId => ({
  type: DELETE_SAVED_SEARCH_SUCCESS,
  payload: { searchId },
});
export const deleteSavedSearchError = e => ({
  type: DELETE_SAVED_SEARCH_ERROR,
  error: true,
  payload: e,
});

export const updateSavedSearchRequest = () => ({
  type: UPDATE_SAVED_SEARCH_REQUEST,
});
export const updateSavedSearchSuccess = savedSearch => ({
  type: UPDATE_SAVED_SEARCH_SUCCESS,
  payload: { savedSearch },
});
export const updateSavedSearchError = e => ({
  type: UPDATE_SAVED_SEARCH_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchSavedSearches = () => (dispatch, getState, sdk) => {
  dispatch(fetchSavedSearchesRequest());

  // For now, we'll store saved searches in the user's private data
  // In a real implementation, you might want to use a separate API endpoint
  const { currentUser } = getState().user;

  if (!currentUser) {
    // If user is not loaded yet, return empty array and let component handle loading
    const savedSearches = [];
    dispatch(fetchSavedSearchesSuccess(savedSearches));
    return Promise.resolve(savedSearches);
  }

  const savedSearches =
    currentUser.attributes?.profile?.privateData?.savedSearches || [];

  // Validate and filter saved searches to ensure they have required properties
  const validSavedSearches = Array.isArray(savedSearches)
    ? savedSearches.filter(
        search =>
          search &&
          typeof search === 'object' &&
          search.id &&
          search.searchPhrase &&
          search.createdAt
      )
    : [];

  // Return a promise that resolves after dispatching success
  return new Promise(resolve => {
    setTimeout(() => {
      dispatch(fetchSavedSearchesSuccess(validSavedSearches));
      dispatch(updateSavedSearchesCount(validSavedSearches.length));
      resolve(validSavedSearches);
    }, 100);
  });
};

export const saveSearch = (searchPhrase, enableNotifications = false) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(saveSearchRequest());

  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(
      saveSearchError(storableError(new Error('User not authenticated')))
    );
    return;
  }

  // Validate input
  if (
    !searchPhrase ||
    typeof searchPhrase !== 'string' ||
    !searchPhrase.trim()
  ) {
    dispatch(
      saveSearchError(storableError(new Error('Search phrase is required')))
    );
    return;
  }

  const existingSavedSearches =
    currentUser.attributes?.profile?.privateData?.savedSearches || [];

  // Ensure existing searches are valid objects with IDs
  const validExistingSearches = existingSavedSearches.filter(
    search => search && typeof search === 'object' && search.id
  );

  const newSearch = {
    id: Date.now().toString(), // Simple ID generation
    searchPhrase: searchPhrase.trim(),
    enableNotifications: Boolean(enableNotifications),
    createdAt: new Date().toISOString(),
  };

  const updatedSavedSearches = [...validExistingSearches, newSearch];

  // Update user's private data
  return sdk.currentUser
    .updateProfile({
      privateData: {
        ...currentUser.attributes.profile.privateData,
        savedSearches: updatedSavedSearches,
      },
    })
    .then(response => {
      dispatch(saveSearchSuccess(newSearch));
      dispatch(updateSavedSearchesCount(updatedSavedSearches.length));
      return response;
    })
    .catch(e => {
      dispatch(saveSearchError(storableError(e)));
      throw e;
    });
};

export const deleteSavedSearch = searchId => (dispatch, getState, sdk) => {
  dispatch(deleteSavedSearchRequest());

  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(
      deleteSavedSearchError(storableError(new Error('User not authenticated')))
    );
    return;
  }

  const existingSavedSearches =
    currentUser.attributes?.profile?.privateData?.savedSearches || [];

  // Validate existing searches and filter out the one to delete
  const validExistingSearches = existingSavedSearches.filter(
    search => search && typeof search === 'object' && search.id
  );

  // Ensure searchId is a string for comparison
  const searchIdStr = String(searchId);

  const updatedSavedSearches = validExistingSearches.filter(
    search => String(search.id) !== searchIdStr
  );

  return sdk.currentUser
    .updateProfile({
      privateData: {
        ...currentUser.attributes.profile.privateData,
        savedSearches: updatedSavedSearches,
      },
    })
    .then(response => {
      dispatch(deleteSavedSearchSuccess(searchId));
      dispatch(updateSavedSearchesCount(updatedSavedSearches.length));
      return response;
    })
    .catch(e => {
      dispatch(deleteSavedSearchError(storableError(e)));
      throw e;
    });
};

export const updateSavedSearch = (searchId, updates) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(updateSavedSearchRequest());

  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(
      updateSavedSearchError(storableError(new Error('User not authenticated')))
    );
    return Promise.reject(new Error('User not authenticated'));
  }

  const existingSavedSearches =
    currentUser.attributes?.profile?.privateData?.savedSearches || [];

  const searchIdStr = String(searchId);
  const updatedSavedSearches = existingSavedSearches.map(search => {
    if (search && String(search.id) === searchIdStr) {
      return { ...search, ...updates };
    }
    return search;
  });

  return sdk.currentUser
    .updateProfile({
      privateData: {
        ...currentUser.attributes.profile.privateData,
        savedSearches: updatedSavedSearches,
      },
    })
    .then(response => {
      const updatedSearch = updatedSavedSearches.find(
        search => String(search.id) === searchIdStr
      );
      dispatch(updateSavedSearchSuccess(updatedSearch));
      return response;
    })
    .catch(e => {
      dispatch(updateSavedSearchError(storableError(e)));
      throw e;
    });
};

// ================ Page load data ================ //

export const loadData = (params, search) => dispatch => {
  return dispatch(fetchSavedSearches());
};
